import { type ToolCall as AgUiToolCall } from '@ag-ui/client'
import { type CopilotPageElement } from 'shared/copilot/pageContext'
import { type CopilotSearchSourcePage } from '@precis-digital/kurama/MessageLayout'

export type { CopilotToolDataResult } from 'shared/copilot/utils/toolDataWait'

/** Page-specific resolvers for human-readable action detail labels. Register via CopilotContext. */
export interface CopilotActionDisplayResolvers {
  getMetricLabel?: (metricId: string) => string
}

export interface CopilotThinkingTitles {
  pastTense: string
  presentTense: string
}

export type CopilotVisibleRenderKind =
  | 'change_url'
  | 'highlight_element'
  | 'suggest_data_source'
  | 'submit_plan_for_approval'
  | 'confirm_action'
  | 'generic'

/**
 * Where a visible tool-call card is placed in the copilot bubble.
 * - `inline` (default): stream order — inserted when the tool result arrives
 * - `message_end`: deferred until the bubble flushes — after thinking + text
 */
export type CopilotVisiblePlacement = 'inline' | 'message_end'

/** Optional overrides for action-page labels derived from tool-call arguments. */
export interface CopilotActionPageLabelsFromArgs {
  actionLabel?: string
  thinkingTitles?: CopilotThinkingTitles
}

export type CopilotToolPresentation =
  | {
      kind: 'action_page'
      actionLabel: string
      thinkingTitles: CopilotThinkingTitles
      hasHighlight?: boolean
      /**
       * When the same tool can open UI or apply changes, resolve labels from args
       * so the thinking step matches the result banner.
       */
      resolveFromArgs?: (args: Record<string, unknown> | undefined) => CopilotActionPageLabelsFromArgs
    }
  | {
      kind: 'action_data_fetch'
      actionLabel: string
      thinkingTitles: CopilotThinkingTitles
      /**
       * When one fetch tool covers several products (e.g. run_cirrus_report across
       * Attribution / Budget Optimiser / …), resolve labels from args.
       */
      resolveFromArgs?: (args: Record<string, unknown> | undefined) => CopilotActionPageLabelsFromArgs
    }
  | {
      kind: 'action_data_analysis'
      actionLabel: string
      thinkingTitles: CopilotThinkingTitles
    }
  | {
      kind: 'thinking_search'
      thinkingTitles: CopilotThinkingTitles
      getSearchTerm: (toolCall: AgUiToolCall) => string
      /** Optional source pages for Kurama expandable search details. */
      getSearchDetails?: (
        toolCall: AgUiToolCall,
        toolResultContent: string | undefined
      ) => CopilotSearchSourcePage[] | undefined
    }
  | {
      kind: 'thinking_inspect_page'
      thinkingTitles: CopilotThinkingTitles
      description?: string
      resolveSignature?: (
        toolCall: AgUiToolCall,
        hasResult: boolean,
        elements: CopilotPageElement[] | undefined
      ) => { title: string; description: string }
    }
  | {
      kind: 'thinking_inspect_source'
      thinkingTitles: CopilotThinkingTitles
      description?: string
      getDescription?: (toolCall: AgUiToolCall) => string
      /**
       * When the same tool name is shared across platforms (e.g. get_account_info
       * for Google Ads / Meta / Merchant Center), resolve titles from args.
       */
      getThinkingTitles?: (toolCall: AgUiToolCall) => CopilotThinkingTitles
    }
  | {
      kind: 'visible'
      title: string
      visibleRenderKind: CopilotVisibleRenderKind
      /** Defaults to `inline`. Use `message_end` for cards that must follow assistant prose. */
      placement?: CopilotVisiblePlacement
    }
  | {
      /**
       * Intentionally silent in the Alvie transcript (no thinking step, no card).
       * Prefer this over omitting the tool so unknown tools can be detected.
       */
      kind: 'hidden'
    }

export interface CopilotToolDefinition {
  presentation: CopilotToolPresentation
}

/**
 * Resolves the action card label from a tool presentation config.
 * Falls back to a generic label when the tool is not configured as an action.
 * For `action_page` tools, `resolveFromArgs` can override the static `actionLabel`.
 */
export const getActionLabelFromPresentation = (
  presentation: CopilotToolPresentation | undefined,
  args?: Record<string, unknown>
): string => {
  if (
    presentation?.kind === 'action_page' ||
    presentation?.kind === 'action_data_fetch' ||
    presentation?.kind === 'action_data_analysis'
  ) {
    if (presentation.kind === 'action_page' || presentation.kind === 'action_data_fetch') {
      const resolvedLabel = presentation.resolveFromArgs?.(args)?.actionLabel
      if (resolvedLabel != null) return resolvedLabel
    }
    return presentation.actionLabel
  }

  return 'Completed action:'
}
